import { Button, Card, Chip, Spinner, SuccessIcon } from "@heroui/react"
import type { SelfOrderStatus } from "@ramoo/contracts"
import { tamu } from "@ramoo/i18n/tamu"
import { rupiah } from "@ramoo/self-order-ui"
import { type ReactNode, useEffect, useState } from "react"
import { statusPesanan } from "./api"

const teks = tamu()

/** Jarak polling. Sama dengan layar bayar: cukup terasa hidup, tidak membanjiri jalur publik. */
const SELANG_MS = 6000

/** Warna keping tahap: warna status hanya untuk maknanya; tahap yang masih berjalan netral. */
const WARNA_BARIS: Record<string, "success" | "danger" | "accent" | "default"> = {
  queued: "default",
  preparing: "accent",
  ready: "success",
  served: "success",
  cancelled: "danger",
}

const WARNA_PESANAN: Record<string, "success" | "danger" | "warning"> = {
  paid: "success",
  void: "danger",
}

/**
 * Pesanan terkirim sekaligus lacak pesanan (mockup Modul 12) — dulu dua layar: "Pesanan terkirim"
 * dengan tombol Bayar, lalu Lacak yang menulis "Menunggu pembayaran" tanpa tombol bayar dan tanpa
 * total.
 *
 * Urutannya mengikuti yang ditanyakan tamu: sudah terkirim belum (kepala), berapa dan bagaimana
 * membayarnya (kartu bayar), lalu punya saya sudah dibuat belum (tahap tiap baris). Satu status
 * pesanan tak pernah bisa mengatakan kopinya sudah siap sementara nasi gorengnya belum, jadi
 * tahapnya tetap per baris.
 */
export function Lacak({
  outletId,
  token,
  orderId,
  receiptNumber,
  kepala,
  poin,
  onBayar,
  onPesanLagi,
}: {
  outletId: string
  token: string
  orderId: string
  receiptNumber: string
  /** Kepala halaman tamu (nama toko + pil meja), sama dengan katalog. */
  kepala: ReactNode
  /** "N poin dipakai · hemat RpX" kalau pesanan ini menukar poin. */
  poin: string | null
  onBayar: () => void
  onPesanLagi: () => void
}) {
  const [data, setData] = useState<SelfOrderStatus | null>(null)
  const [galat, setGalat] = useState<string | null>(null)

  useEffect(() => {
    let batal = false
    /* Dipegang di dalam efek, bukan dibaca dari state: `data` di daftar dependensi akan membongkar
       dan memasang ulang interval setiap kali status datang, jadi jaraknya tak pernah benar. */
    let pernahDapat = false
    const tarik = async () => {
      try {
        const r = await statusPesanan(outletId, token, orderId)
        if (batal) return
        pernahDapat = true
        setData(r)
        setGalat(null)
      } catch (e) {
        /* Gagal sekali tidak mengosongkan layar: yang sudah tampil tetap benar sampai ada yang
           lebih baru. Tamu di wifi kafe kehilangan satu tarikan itu hal biasa. */
        if (!batal && !pernahDapat) setGalat((e as Error).message)
      }
    }
    void tarik()
    const t = setInterval(tarik, SELANG_MS)
    return () => {
      batal = true
      clearInterval(t)
    }
  }, [outletId, token, orderId])

  const bisaBayar = !!data && data.status !== "paid" && data.status !== "void"

  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col pb-24">
      {kepala}
      <main className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-success-soft text-success-soft-foreground">
            <SuccessIcon className="size-5" />
          </span>
          <span className="flex min-w-0 flex-col">
            <h1 className="text-lg leading-7 font-semibold">{teks.terkirim}</h1>
            <span className="text-sm text-muted">
              {teks.nomor} <b className="font-semibold text-foreground">{receiptNumber}</b>
            </span>
          </span>
        </div>

        <Card className="gap-3 p-3.5">
          {data ? (
            <>
              <div className="flex items-center justify-between gap-3">
                <Chip color={WARNA_PESANAN[data.status] ?? "warning"} variant="soft" size="sm">
                  {teks.statusPesanan[data.status] ?? data.status}
                </Chip>
                <b className="text-lg font-semibold tabular-nums">{rupiah(data.total)}</b>
              </div>
              {poin ? <p className="text-xs text-muted">{poin}</p> : null}
              {bisaBayar ? (
                <>
                  <Button fullWidth size="lg" onPress={onBayar}>
                    {teks.bayarQris}
                  </Button>
                  <p className="text-center text-xs text-muted">{teks.bayarDiKasir}</p>
                </>
              ) : null}
            </>
          ) : galat ? (
            <p className="text-sm text-muted">{galat}</p>
          ) : (
            <div className="flex justify-center py-2">
              <Spinner size="sm" />
            </div>
          )}
        </Card>

        {data?.lines.length ? (
          <section className="flex flex-col">
            <h2 className="pb-1 text-base leading-6 font-semibold">{teks.isiPesanan}</h2>
            {data.lines.map((l, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: server mengirim urut `sortOrder` dan urutannya tidak pernah berubah
                key={`${l.name}-${i}`}
                className="flex min-h-11 items-center justify-between gap-3 border-b border-separator py-2 last:border-b-0"
              >
                <span className="min-w-0 text-sm">
                  {l.quantity}× {l.name}
                  {l.variantName ? ` — ${l.variantName}` : ""}
                </span>
                <Chip color={WARNA_BARIS[l.status] ?? "default"} variant="soft" size="sm">
                  {teks.statusBaris[l.status] ?? l.status}
                </Chip>
              </div>
            ))}
          </section>
        ) : null}
        <p className="text-center text-xs text-muted">{teks.perbaruiSendiri}</p>
      </main>
      <div className="fixed inset-x-0 bottom-0 mx-auto max-w-xl border-t border-separator bg-surface p-3 pb-[calc(--spacing(3)+env(safe-area-inset-bottom))]">
        <Button fullWidth variant="outline" onPress={onPesanLagi}>
          {teks.pesanLagi}
        </Button>
      </div>
    </div>
  )
}
